// https://stackoverflow.com/questions/46176165/ways-to-get-string-literal-type-of-array-values-without-enum-overhead
import { CSSProperties, ReactNode } from 'react';
import classNames from 'classnames';
import * as Classes from './classes';
import { Col } from '../row';

export const tuple = <T extends string[]>(...args: T) => args;

/** https://github.com/Microsoft/TypeScript/issues/29729 */
export type LiteralUnion<T extends U, U> = T | (U & {});

/**
 * https://stackoverflow.com/a/59187769 Extract the type of an element of an array/tuple without
 * performing indexing
 */
export type ElementOf<T> = T extends (infer E)[]
  ? E
  : T extends readonly (infer F)[]
  ? F
  : never;

export interface IProps {
  /** Seznam class name oddělených mezerou, které jsou předané do renderované komponenty. */
  className?: string;
  style?: CSSProperties;
  title?: string;
  children?: ReactNode | undefined;
  dataTest?: string;
}

export interface IInputProps {
  autocomplete?: string;
}

export interface IInputSearchProps {
  /**
   * Field může podporovat potvrzení hodnoty položky, typicky pro možnost např. hledání ve filtrech. Většina implementací volá tuto metodu na enter.
   */
  onSearch?: () => void;
}

export interface IGridProps extends React.ComponentProps<typeof Col> {}
export interface IAddonsProps {
  addonAfter?: ReactNode;
  addonAfterCompact?: boolean;
}

export declare type BaseProps = IProps;

export declare type BaseInputProps = IInputProps;

export interface IActionProps extends BaseProps {
  /** Zda má být komponenta zakázaná. */
  disabled?: boolean;

  /** Komponenta ikony, typicky font awesome. */
  icon?: JSX.Element;
  /**
   * Ikona napravo, ve většině případů se nepoužívá, jedno z mála použití je např. tlačítko "Další >".
   * Tato properta funguje pouye na NE dropdown tlačítka.
   */
  iconRight?: JSX.Element;

  /** Click event handler. */
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

/**
 * Základní typy informace.
 */
export const IntentStyles = tuple(
  'none',
  'success',
  'warning',
  'danger',
  'primary'
);
export type IntentStyle = ElementOf<typeof IntentStyles>;
export const ProgressStyles = tuple('none', 'small', 'large');
export type ProgressStyle = ElementOf<typeof ProgressStyles>;

export const GroupContainerTypes = tuple('default', 'tagHeader', 'noHeader');
export type GroupContainerType = ElementOf<typeof GroupContainerTypes>;

export const TabsTypes = tuple('default', 'process');
export type TabsType = ElementOf<typeof TabsTypes>;

export const AppLayoutDetailHeaderIntents = tuple('default', 'secondary');
export type AppLayoutDetailHeaderIntent = ElementOf<
  typeof AppLayoutDetailHeaderIntents
>;

export const CalloutIntentStyles = tuple(
  'none',
  'default',
  'info',
  'success',
  'warning',
  'danger'
);
export type CalloutIntentStyle = ElementOf<typeof CalloutIntentStyles>;

export const PopupIntentStyles = tuple('none', 'success', 'warning', 'danger');
export type PopupIntentStyle = ElementOf<typeof PopupIntentStyles>;

export const ViewIntents = tuple(
  'inherit',
  'default',
  'success',
  'warning',
  'danger',
  'primary',
  'secondary',
  'inactive'
);
export type ViewIntent = ElementOf<typeof ViewIntents>;

export const TileIntents = tuple(
  'default',
  'success',
  'warning',
  'danger',
  'inactive'
);
export type TileIntent = ElementOf<typeof TileIntents>;

export const MenuItemIntents = tuple('default', 'success', 'warning', 'danger');
export type MenuItemIntent = ElementOf<typeof MenuItemIntents>;

export const TagIntents = tuple(
  'default',
  'success',
  'warning',
  'danger',
  'primary',
  'secondary',
  'inactive'
);
export type TagIntent = ElementOf<typeof TagIntents>;

export const LayoutModes = tuple('default', 'viewDetail');
export type LayoutMode = ElementOf<typeof LayoutModes>;

export const BadgeIntents = tuple(
  'default',
  'success',
  'warning',
  'danger',
  'primary',
  'secondary',
  'inactive'
);
export type BadgeIntent = ElementOf<typeof BadgeIntents>;

export const ListItemIntents = tuple(
  'default',
  'default-inverse',
  'success',
  'warning',
  'danger',
  'secondary',
  'inactive'
);
export type ListItemIntent = ElementOf<typeof ListItemIntents>;

export const BadgeCountIntents = tuple(
  'default',
  'danger',
  'warning',
  'success',
  'secondary'
);
export type BadgeCountIntent = ElementOf<typeof BadgeCountIntents>;

export const DividerIntents = tuple('default', 'secondary', 'warning');
export type DividerIntent = ElementOf<typeof DividerIntents>;
export const DividerMods = tuple('default', 'dashed');
export type DividerMode = ElementOf<typeof DividerMods>;
export const CardIntentStyles = tuple(
  'none',
  'warning',
  'danger',
  'inactive',
  'success',
  'info'
);
export type CardIntentStyles = ElementOf<typeof CardIntentStyles>;
export const BlockIntentStyles = tuple('none', 'inactive');
export type BlockIntentStyles = ElementOf<typeof BlockIntentStyles>;

export const ConfirmIntents = tuple('none', 'danger');
export type ConfirmIntent = ElementOf<typeof ConfirmIntents>;
export const InfoIntents = tuple('primary', 'warning');
export type InfoIntent = ElementOf<typeof InfoIntents>;
export const TagHeights = tuple('default', 'inherit');
export type TagHeight = ElementOf<typeof TagHeights>;
// export const IntentStyle = {
//   NONE: 'none' as 'none',
//   SUCCESS: 'success' as 'success',
//   WARNING: 'warning' as 'warning',
//   DANGER: 'danger' as 'danger',
//   PRIMARY: 'primary' as 'primary',
// };
//
// export type IntentStyle = typeof IntentStyle[keyof typeof IntentStyle];

/**
 * Typ módu komponenty.
 */
export const Modes = tuple('default', 'edit', 'view');
export type Mode = ElementOf<typeof Modes>;

export const ListModes = tuple(
  'default',
  'edit',
  'view',
  'labels',
  'input',
  'cards',
  'grid',
  'form'
);
export type ListMode = ElementOf<typeof ListModes>;

export const TableModes = tuple('default', 'edit', 'view', 'input');
export type TableMode = ElementOf<typeof TableModes>;

export const CheckableModes = tuple('radio');
export type CheckableType = boolean | ElementOf<typeof CheckableModes>;

export const DataTableModes = tuple('default', 'input');
export type DataTableMode = ElementOf<typeof DataTableModes>;

export const VirtualListModes = tuple('default', 'input');
export type VirtualListMode = ElementOf<typeof VirtualListModes>;

export const CardModes = tuple(
  'default',
  'edit',
  'view',
  'section',
  'subsection',
  'filter'
);
export type CardMode = ElementOf<typeof CardModes>;
export const CloseButtonModes = tuple('edit', 'view');
export type CloseButtonMode = ElementOf<typeof CloseButtonModes>;

// export const Mode = {
//   DEFAULT: 'default' as 'default',
//   EDIT: 'edit' as 'edit',
//   VIEW: 'view' as 'view',
// };
//
// // eslint-disable-next-line @typescript-eslint/no-redeclare
// export type Mode = typeof Mode[keyof typeof Mode];

export const TextTypes = tuple(
  'card',
  'block',
  'list',
  'listItem',
  'tile',
  'tileValue',
  // @deprecated nahrazeno detail
  'detailHeader',
  'detail',
  // @deprecated nahrazeno detail2
  'detail1',
  'detail2',
  // @deprecated nahrazeno detail3
  'detailBlock',
  // @deprecated nahrazeno page
  'pageHeader',
  'page',
  // @deprecated nahrazeno page2
  'page1',
  'page2',
  // @deprecated nahrazeno page3
  'pageBlock',
  'sidePanel',
  'section',
  'value'
);
export type TextType = ElementOf<typeof TextTypes>;

export const getClassByTextType = (type?: TextType) => {
  return classNames({
    [Classes.TEXT_TYPE_CARD]: type === 'card',
    [Classes.TEXT_TYPE_BLOCK]: type === 'block',
    [Classes.TEXT_TYPE_LIST]: type === 'list',
    [Classes.TEXT_TYPE_LIST_ITEM]: type === 'listItem',
    [Classes.TEXT_TYPE_DETAIL]: type === 'detail' || type === 'detailHeader',
    [Classes.TEXT_TYPE_DETAIL2]: type === 'detail1' || type === 'detail2',
    [Classes.TEXT_TYPE_PAGE]: type === 'page' || type === 'pageHeader',
    [Classes.TEXT_TYPE_PAGE2]: type === 'page2' || type === 'page1',
    [Classes.TEXT_TYPE_SIDEPANEL]: type === 'sidePanel',
    [Classes.TEXT_TYPE_TILE]: type === 'tile',
    [Classes.TEXT_TYPE_TILE_VALUE]: type === 'tileValue',
    [Classes.TEXT_TYPE_SECTION]: type === 'section',
    [Classes.TEXT_TYPE_VALUE]: type === 'value',
  });
};

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export const KalendarIntentStyles = tuple(
  'color-1',
  'color-2',
  'color-3',
  'color-4',
  'color-5'
);
export type KalendarIntentStyle = ElementOf<typeof KalendarIntentStyles>;
